import { SubpageShell } from '../../components/layout/SubpageShell';
import { CtaBand, FeatureRow, SectionHead, SubHero } from '../../components/subpage/blocks';
import { primaryButton, secondaryButton } from '../../components/subpage/styles';
import { eklentiPage as copy } from '../../content/products/eklenti';
import { useTicker } from '../../hooks/useMotion';
import { asset } from '../../lib/asset';
import { Breakdown, DemoScreen, OrdersScreen } from './EklentiScreens';
import { DETAIL_PRODUCT } from './eklentiOrnek';

/*
 * Sayfa akışı (2 Kasım): hero'da Trendyol "Siparişlerim" ekranı ve sipariş rozetinin balonu → dört ekran kartı →
 * etkileşimli ön gösterim (Siparişlerim / Ürünlerim / Kampanyalar sekmeleri, rozet üstünde balon; tam genişlik) →
 * "Neyi hesaba katar?" + ürün sayfası kâr kutusu → CTA.
 * Ekran verisi ve hesabı `eklentiOrnek.ts`, ekranlar `EklentiScreens.tsx`.
 */
/** Eklenti penceresindeki anahtar: açık = mavi, kapalı = sönük. Dekoratif. */
function Toggle({ on }: { on: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={`relative inline-block h-[18px] w-[32px] shrink-0 rounded-full transition-colors ${on ? 'bg-[#2f6fe4]' : 'bg-white/[0.08]'}`}
    >
      <i className={`absolute top-[2px] h-[14px] w-[14px] rounded-full bg-white ${on ? 'right-[2px]' : 'left-[2px] opacity-40'}`} />
    </span>
  );
}

/**
 * Çalıştığı pazaryerleri: dört kart, her biri eklentinin açılır penceresindeki pazaryeri satırının karşılığı
 * (logo, kaç ekranın açık olduğu, "sayfada gösterilecekler" anahtarları). Logosu olmayan marka adıyla yazılır.
 */
function Marketplaces() {
  const m = copy.marketplaces;
  return (
    <section className="mx-auto flex max-w-[1200px] flex-col gap-10 px-4 pt-24 sm:px-6 lg:pt-[110px]">
      <SectionHead kicker={m.kicker} title={m.title} titleAccent={m.titleAccent} desc={m.lead} />
      <ul className="m-0 grid list-none gap-[18px] p-0 sm:grid-cols-2 lg:grid-cols-4">
        {m.items.map((it, i) => {
          const count = it.on.filter(Boolean).length;
          return (
            <li key={it.name} className="zh-pg-card zh-reveal flex flex-col gap-4" style={{ transitionDelay: `${i * 0.08}s` }}>
              <div className="flex items-center justify-between gap-3">
                <span className="flex h-7 items-center gap-2 font-display text-[18px] font-semibold text-white">
                  {'src' in it && it.src ? (
                    'mark' in it && it.mark ? (
                      <>
                        <img src={asset(it.src)} alt="" className="h-6 w-6 rounded-[6px]" />
                        {it.name}
                      </>
                    ) : (
                      <img src={asset(it.src)} alt={it.name} className="h-5 w-auto" />
                    )
                  ) : (
                    it.name
                  )}
                </span>
                <span className="rounded-full border border-white/[0.1] bg-white/[0.05] px-2 py-0.5 text-[12px] font-bold tabular-nums text-ink-soft">
                  {count}/{it.on.length}
                </span>
              </div>
              <span className="text-[11.5px] font-bold text-ink-subdued">{m.shown}</span>
              {/* Telefonda anahtarlar 2×2 (dört kart alt alta 1200px tutuyordu), `sm`den itibaren alt alta liste. */}
              <ul className="m-0 -mt-2 grid list-none grid-cols-2 gap-2 p-0 sm:flex sm:flex-col">
                {m.features.map((f, k) => (
                  <li
                    key={f}
                    className={`flex items-center justify-between gap-2 rounded-[10px] border px-2.5 py-2 text-[12px] font-semibold sm:gap-3 sm:px-3 sm:text-[13px] ${
                      it.on[k] ? 'border-[rgba(69,192,238,0.25)] bg-[rgba(69,192,238,0.06)] text-white' : 'border-white/[0.06] text-ink-subdued'
                    }`}
                  >
                    {f}
                    <Toggle on={it.on[k]} />
                  </li>
                ))}
              </ul>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export default function EklentiPage() {
  const { tick, still } = useTicker(3600);
  const { hero } = copy;

  return (
    <SubpageShell pageKey={copy.href}>
      <SubHero
        accent="#45c0ee"
        accentOpacity={0.18}
        crumb={hero.crumb}
        title={hero.title}
        titleAccent={hero.titleAccent}
        lead={hero.lead}
        // Sağ sütun geniş: `xl`de pencere + yanındaki balon birlikte sığsın.
        cols="lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)]"
        actions={
          <>
            <p className="m-0 text-[15px] font-bold text-[#2ad5c4]">{hero.included}</p>
            <p className="m-0 -mt-2 text-[14px] text-ink-subdued">{hero.marketplaces}</p>
            <div className="mt-1 flex flex-wrap gap-3">
              <a href={hero.primary.href} className={primaryButton}>
                {hero.primary.label}
              </a>
              <a href={hero.secondary.href} className={secondaryButton}>
                {hero.secondary.label}
              </a>
            </div>
          </>
        }
        // Pencere telefonda gizli (`sm` altı): beyaz tablo 390px'te okunmuyordu; metin ve düğmeler yeter.
        visual={
          <div className="hidden min-w-0 sm:block">
            <OrdersScreen tick={tick} still={still} />
            <p className="m-0 mt-4 text-[12px] leading-[1.5] text-ink-subdued xl:pr-[276px]">{hero.note}</p>
          </div>
        }
      />

      <Marketplaces />

      <section className="mx-auto flex max-w-[1200px] flex-col gap-10 px-4 pt-24 sm:px-6 lg:pt-[110px]">
        <SectionHead title={copy.where.title} titleAccent={copy.where.titleAccent} />
        <div className="grid gap-[18px] sm:grid-cols-2 lg:grid-cols-4">
          {copy.where.items.map((w, i) => (
            <div key={w.title} className="zh-pg-card zh-reveal flex flex-col gap-3" style={{ transitionDelay: `${i * 0.08}s` }}>
              <span className="flex h-12 w-12 items-center justify-center rounded-[14px] border border-[rgba(69,192,238,0.3)] bg-[rgba(69,192,238,0.12)] text-[#45c0ee] shadow-[0_0_24px_rgba(69,192,238,0.18)]">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d={w.icon} />
                </svg>
              </span>
              <h3 className="m-0 font-display text-[19px] font-semibold tracking-[-0.01em] text-white">{w.title}</h3>
              <p className="m-0 text-[14.5px] leading-[1.6] text-ink-soft">{w.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Ön gösterim yalnız `sm` ve üstünde: telefonda sekmeli tablo ve rozet balonu okunmuyor, bölüm bütünüyle gizli. */}
      <section className="mx-auto hidden max-w-[1200px] flex-col gap-10 px-4 pt-24 sm:flex sm:px-6 lg:pt-[110px]">
        <SectionHead kicker={copy.campaign.kicker} title={copy.campaign.title} titleAccent={copy.campaign.titleAccent} desc={copy.campaign.lead} />
        <div className="zh-reveal">
          <DemoScreen />
          <p className="m-0 mt-4 text-center text-[12px] text-ink-subdued">{copy.campaign.note}</p>
        </div>
      </section>

      <FeatureRow kicker={copy.costs.kicker} title={copy.costs.title} titleAccent={copy.costs.titleAccent} lead={copy.costs.lead} checks={copy.costs.checks}>
        <Breakdown name={DETAIL_PRODUCT.name} c={DETAIL_PRODUCT.c} caption={copy.costs.screenTitle} />
      </FeatureRow>

      <CtaBand {...copy.cta} />
    </SubpageShell>
  );
}
